import { jsPDF } from 'jspdf';
import Select from 'react-select';
import useAuth from 'hooks/useAuth';
import html2canvas from 'html2canvas';
import VendaLoja from './graficos/vendaLoja';
import { useParams } from 'react-router-dom';
import BackButton from '../sistema/backButton';
import DateColumn from '../sistema/dateColumn';
import baseController from 'api/base.controller';
import ResumoEvento from './graficos/resumoEvento';
import ReloadButton from '../sistema/reloadButton';
import DetailButton from '../sistema/detailButton';
import ResumoProduto from './graficos/resumoProduto';
import Error401 from 'components/pages/errors/Error401';
import DownloadPDFButton from '../sistema/downloadPDFButton';
import FullScreenLoading from '../sistema/fullScreenLoading';
import VendaPorHoraEvento from './graficos/vendaPorHoraEvento';
import VendaFormaPagamento from './graficos/vendaFormaPagamento';
import FieldValidationMessage from '../sistema/fieldValidationMessage';
import { Row, Col, Card, Form, Button, Spinner } from 'react-bootstrap';
import { defaultDashboardRoles } from 'components/pages/sistema/allowedRoles';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

const formaPagamentoRadius = ['70%', '100%'];

const SectionLoadingStatus = React.memo(({ isLoading }) => {
  if (!isLoading) {
    return null;
  }

  return (
    <div className='d-flex align-items-center justify-content-center gap-2 py-5'>
      <Spinner animation='border' size='sm' />
      <small>Carregando...</small>
    </div>
  );
});

const MemoResumoEventoSection = React.memo(({ data, isLoading }) => (
  isLoading
    ? <SectionLoadingStatus isLoading={isLoading} />
    : <ResumoEvento data={data ?? []} />
));

const MemoVendaLojaSection = React.memo(({ data, isLoading }) => (
  isLoading
    ? <SectionLoadingStatus isLoading={isLoading} />
    : <VendaLoja data={data} />
));

const MemoVendaFormaPagamentoSection = React.memo(({ data, isLoading }) => (
  isLoading
    ? <SectionLoadingStatus isLoading={isLoading} />
    : <VendaFormaPagamento data={data} radius={formaPagamentoRadius} />
));

const MemoVendaPorHoraSection = React.memo(({ data, isLoading }) => (
  isLoading
    ? <SectionLoadingStatus isLoading={isLoading} />
    : <VendaPorHoraEvento data={data} />
));

const MemoResumoProdutoSection = React.memo(({ data, isLoading }) => (
  isLoading
    ? <SectionLoadingStatus isLoading={isLoading} />
    : <ResumoProduto data={data} showDetails />
));

const EventoDashboard = () => {
  const { id } = useParams();
  const { auth } = useAuth();
  const abortControllerRef = useRef(null);
  const initialLoadDoneRef = useRef(false);
  const [lojasIds, setLojasIds] = useState([]);
  const [valueLoja, setValueLoja] = useState(null);
  const [eventoInfo, setEventoInfo] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [vendaLojaData, setVendaLojaData] = useState([]);
  const [vendaHoraData, setVendaHoraData] = useState([]);
  const [comboLojaValues, setComboLojaValues] = useState([]);
  const [resumoEventoData, setResumoEventoData] = useState([]);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [resumoProdutoData, setResumoProdutoData] = useState([]);
  const [formaPagamentoData, setFormaPagamentoData] = useState([]);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [sectionLoading, setSectionLoading] = useState({
    resumoEvento: false,
    vendaLoja: false,
    vendaHora: false,
    eventoInfo: false,
    resumoProduto: false,
    formaPagamento: false,
    lojas: false
  });

  const [activeFilter, setActiveFilter] = useState('last30');

  const [finalDate, setFinalDate] = useState('');
  const [initialDate, setInitialDate] = useState('');
  const [filterChanged, setFilterChanged] = useState(false);

  const headers = useMemo(() => ({
    Authorization: auth.token ?? JSON.parse(localStorage.getItem('auth')).token
  }), [auth.token]);

  const adjustUniqueLojaDataToShow = useCallback((loja) => ({
    value: loja.id,
    nome: loja.nome,
    label: loja.nome,
  }), []);

  const adjustLojaDataToCombo = useCallback((data) => {
    return data.map(adjustUniqueLojaDataToShow);
  }, [adjustUniqueLojaDataToShow]);

  const definePeriod = useCallback((period, subtractFinal = 0) => {
    const final = new Date();
    const initial = new Date();

    initial.setDate(initial.getDate() - period);
    initial.setHours(0, 0, 0, 0);

    setInitialDate(initial);

    final.setDate(final.getDate() - subtractFinal);
    final.setHours(23, 59, 59, 999);

    setFinalDate(final);
  }, []);

  const handleDateFilter = useCallback((period) => {
    switch(period) {
      case 'today': {
        definePeriod(0);
        break;
      }
      case 'yesterday': {
        definePeriod(1, 1);
        break;
      }
      case 'last15': {
        definePeriod(15);
        break;
      }
      case 'last30': {
        definePeriod(30);
        break;
      }
      default: {
        setFinalDate(null);
        setInitialDate(null);
      }
    }

    setFilterChanged(true);
  }, [definePeriod]);

  const processResumoEventoData = useCallback((data, lojasIds) => {
    if (!data) return [];
    
    return data.map(tile => ({
      ...tile,
      valor: tile.valores.reduce((acc, current) => {
        if (lojasIds.length === 0 || lojasIds.includes(current.lojaId)) {
          return acc + current.valor;
        }
        return acc;
      }, 0)
    }));
  }, []);

  const processVendaLojaData = useCallback((data, lojasIds) => {
    if (!data) return [];
    return lojasIds.length > 0 ? data.filter(d => lojasIds.includes(d.lojaId)) : data;
  }, []);

  const processVendaHoraData = useCallback((data, lojasIds) => {
    if (!data || data.length === 0) return [];
    
    const filteredData = lojasIds.length > 0 ? data.filter(d => lojasIds.includes(d.lojaId)) : data;
    const grouped = Object.groupBy(filteredData, ({hora}) => hora);
    
    return Object.keys(grouped).map(key => ({
      hora: key,
      valor: grouped[key].reduce((acc, current) => acc + current.valor, 0)
    }));
  }, []);

  const processResumoProdutoData = useCallback((data, lojasIds) => {
    if (!data) return [];
    
    return data
      .map((d) => ({
        ...d,
        lojas: d.lojas.filter((l) =>
          (lojasIds.length === 0 || lojasIds.includes(l.id)) && l.quantidade > 0
        )
      }))
      .filter((d) => d.lojas.length > 0);
  }, []);

  const processFormaPagamentoData = useCallback((data, lojasIds) => {
    if (!data) return [];
    
    const filteredData = lojasIds.length > 0 ? data.filter(d => lojasIds.includes(d.lojaId)) : data;
    const grouped = Object.groupBy(filteredData, ({formaPagamentoNome}) => formaPagamentoNome);
    
    return Object.keys(grouped).map(key => ({
      formaPagamentoNome: key,
      total: grouped[key].reduce((acc, current) => acc + current.total, 0)
    }));
  }, []);

  const doLoadData = useCallback(async (params) => {
    if (abortControllerRef.current && initialLoadDoneRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const signal = controller.signal;

    setSectionLoading({
      resumoEvento: true,
      vendaLoja: true,
      vendaHora: true,
      eventoInfo: true,
      resumoProduto: true,
      formaPagamento: true,
      lojas: true
    });

    let loadingReleased = false;
    const releaseLoading = () => {
      if (!loadingReleased) {
        loadingReleased = true;
        setIsLoading(false);
      }
    };

    const isAbortError = (error) => error?.name === 'AbortError' || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED';

    const loadSection = async (request, onSuccess, sectionName, sectionKey) => {
      try {
        const response = await request;
        if (!signal.aborted) {
          onSuccess(response.data);
        }
      } catch (error) {
        if (isAbortError(error)) return;
        console.error(`Error loading ${sectionName}:`, error);
      } finally {
        if (!signal.aborted) {
          setSectionLoading((current) => ({ ...current, [sectionKey]: false }));
          releaseLoading();
        }
      }
    };

    try {
      await Promise.allSettled([
        loadSection(
          baseController.get(`Dashboard/ResumoEvento/${id}`, { params, headers, signal }),
          (data) => setResumoEventoData(processResumoEventoData(data, lojasIds)),
          'resumo evento',
          'resumoEvento'
        ),
        loadSection(
          baseController.get(`Dashboard/VendaLojaEvento/${id}`, { params, headers, signal }),
          (data) => setVendaLojaData(processVendaLojaData(data, lojasIds)),
          'venda por loja',
          'vendaLoja'
        ),
        loadSection(
          baseController.get(`Dashboard/VendaHoraEvento/${id}`, { params, headers, signal }),
          (data) => setVendaHoraData(processVendaHoraData(data, lojasIds)),
          'venda por hora',
          'vendaHora'
        ),
        loadSection(
          baseController.get(`Dashboard/GetSpecificEventoInfo/${id}`, { params, headers, signal }),
          (data) => setEventoInfo(data),
          'informacoes do evento',
          'eventoInfo'
        ),
        loadSection(
          baseController.get(`Dashboard/ResumoProdutoEvento/${id}`, { params, headers, signal }),
          (data) => setResumoProdutoData(processResumoProdutoData(data, lojasIds)),
          'resumo de produtos',
          'resumoProduto'
        ),
        loadSection(
          baseController.get(`Dashboard/FormaPagamentoEvento/${id}`, { params, headers, signal }),
          (data) => setFormaPagamentoData(processFormaPagamentoData(data, lojasIds)),
          'forma de pagamento',
          'formaPagamento'
        ),
        loadSection(
          baseController.get(`dashboard/get-evento-lojas/${id}`, { headers, signal }),
          (data) => setComboLojaValues(adjustLojaDataToCombo(data)),
          'lojas do evento',
          'lojas'
        )
      ]);
    } catch (error) {
      if (!isAbortError(error))
        console.error('Error loading dashboard data:', error);
    } finally {
      if (!signal.aborted) {
        releaseLoading();
        initialLoadDoneRef.current = true;
        setInitialLoadDone(true);
        setFilterChanged(false);
      }
    }
  }, [id, headers, lojasIds, adjustLojaDataToCombo, processResumoEventoData, processVendaLojaData, processVendaHoraData, processResumoProdutoData, processFormaPagamentoData]);

  const handleLoadDataCustomFilter = useCallback((e) => {
    e?.preventDefault();
    setIsLoading(true);

    const adjustedFinalDate = new Date(finalDate);
    adjustedFinalDate.setDate(adjustedFinalDate.getDate() + 1);
    adjustedFinalDate.setHours(23, 59, 59);

    const params = {
      dataFinal: adjustedFinalDate,
      dataInicial: initialDate
    };

    doLoadData(params);
  }, [finalDate, initialDate, doLoadData]);

  const loadData = useCallback(async () => {
    if (!initialLoadDoneRef.current) {
      setIsLoading(true);
    }
    const params = {
      dataFinal: finalDate,
      dataInicial: initialDate
    };
    await doLoadData(params);
  }, [finalDate, initialDate, doLoadData]);

  const generatePDF = useCallback(async () => {
    setIsGeneratingPdf(true);

    try {
      const element = document.getElementById('pdf-material');
      if (!element) {
        return;
      }

      await new Promise((resolve) => requestAnimationFrame(resolve));

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: 'mm',
        format: [200, 287],
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let currentY = 0;
      let hasContent = false;

      const sections = Array.from(element.children);

      for (const section of sections) {
        const canvas = await html2canvas(section, {
          scale: 1,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          onclone: (clonedDocument) => {
            const shadowedCards = clonedDocument.querySelectorAll('.dashboard-card-shadow');
            shadowedCards.forEach((card) => {
              card.style.boxShadow = 'none';
              card.style.filter = 'none';
            });
          }
        });

        if (canvas.width === 0 || canvas.height === 0) {
          continue;
        }

        hasContent = true;

        // Convert px height to PDF mm, keeping aspect ratio.
        const sectionHeightMm = (canvas.height * pageWidth) / canvas.width;
        const pageSliceHeightPx = Math.max(1, Math.floor((pageHeight * canvas.width) / pageWidth));

        let sourceY = 0;
        while (sourceY < canvas.height) {
          const sliceHeightPx = Math.min(pageSliceHeightPx, canvas.height - sourceY);

          const sliceCanvas = document.createElement('canvas');
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = sliceHeightPx;
          const sliceContext = sliceCanvas.getContext('2d');

          if (!sliceContext) {
            break;
          }

          sliceContext.drawImage(
            canvas,
            0,
            sourceY,
            canvas.width,
            sliceHeightPx,
            0,
            0,
            canvas.width,
            sliceHeightPx
          );

          const sliceHeightMm = (sliceHeightPx * pageWidth) / canvas.width;
          const sliceImg = sliceCanvas.toDataURL('image/jpeg', 0.9);

          if (currentY + sliceHeightMm > pageHeight && currentY > 0) {
            pdf.addPage();
            currentY = 0;
          }

          pdf.addImage(sliceImg, 'JPEG', 0, currentY, pageWidth, sliceHeightMm);
          currentY += sliceHeightMm;
          sourceY += sliceHeightPx;
        }

        if (sectionHeightMm > 0 && currentY + 4 <= pageHeight) {
          currentY += 4;
        }
      }

      if (!hasContent) {
        throw new Error('Failed to capture dashboard for PDF.');
      }

      pdf.save(`${eventoInfo.nome} ${eventoInfo.local}`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [eventoInfo.nome, eventoInfo.local]);

  const handleTodayClick = useCallback(() => {
    setActiveFilter('today');
    handleDateFilter('today');
  }, [handleDateFilter]);

  const handleYesterdayClick = useCallback(() => {
    setActiveFilter('yesterday');
    handleDateFilter('yesterday');
  }, [handleDateFilter]);

  const handleLast15Click = useCallback(() => {
    setActiveFilter('last15');
    handleDateFilter('last15');
  }, [handleDateFilter]);

  const handleLast30Click = useCallback(() => {
    setActiveFilter('last30');
    handleDateFilter('last30');
  }, [handleDateFilter]);

  const handleWholePeriodClick = useCallback(() => {
    setActiveFilter('wholePeriod');
    handleDateFilter('wholePeriod');
  }, [handleDateFilter]);

  const handleCustomPeriodClick = useCallback(() => {
    setActiveFilter('custom');
  }, []);

  const handleInitialDateChange = useCallback((e) => {
    setInitialDate(e.target.value);
  }, []);

  const handleFinalDateChange = useCallback((e) => {
    setFinalDate(e.target.value);
  }, []);

  const handleLojaChange = useCallback((value) => {
    setValueLoja(value);
  }, []);

  useEffect(() => {
    if (filterChanged) {
      loadData();
    }
  }, [filterChanged, loadData]);

  useEffect(() => {
    const isAuthorized = defaultDashboardRoles.includes(auth.role ?? JSON.parse(localStorage.getItem('auth')).role);
    setAuthorized(isAuthorized);
    
    if (isAuthorized) {
      handleDateFilter('last30');
    }
  }, [auth.role, handleDateFilter]);

  useEffect(() => {
    const newData = valueLoja?.map((l) => l.value) || [];
    setLojasIds((previousData) => {
      const isSameLength = previousData.length === newData.length;
      const hasSameValues = isSameLength && previousData.every((value, index) => value === newData[index]);
      return hasSameValues ? previousData : newData;
    });
  }, [valueLoja]);

  const dashboardContent = useMemo(() => (
    <div id='pdf-material'>
      <Row className="g-1 mb-1">
        <Col className="dashboard-resumo" md={12} xxl={3}>
          <MemoResumoEventoSection data={resumoEventoData} isLoading={sectionLoading.resumoEvento} />
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col lg={6}>
          <MemoVendaLojaSection data={vendaLojaData} isLoading={sectionLoading.vendaLoja} />
        </Col>
        <Col lg={6}>
          <MemoVendaFormaPagamentoSection data={formaPagamentoData} isLoading={sectionLoading.formaPagamento} />
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col lg={12}>
          <MemoVendaPorHoraSection data={vendaHoraData} isLoading={sectionLoading.vendaHora} />
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col lg={12}>
          <MemoResumoProdutoSection data={resumoProdutoData} isLoading={sectionLoading.resumoProduto} />
        </Col>
      </Row>
    </div>
  ), [resumoEventoData, vendaLojaData, formaPagamentoData, vendaHoraData, resumoProdutoData, sectionLoading.resumoEvento, sectionLoading.vendaLoja, sectionLoading.formaPagamento, sectionLoading.vendaHora, sectionLoading.resumoProduto]);

  const filterButtons = useMemo(() => (
    <>
      <Row className='grid-actions dashboard-filter-actions'>
        <Button 
          className='button-date-filter d-inline-block text-truncate' 
          variant={activeFilter === 'today' ? 'success' : 'primary'}
          onClick={handleTodayClick}
        >
          Hoje
        </Button>

        <Button 
          className='button-date-filter'
          variant={activeFilter === 'yesterday' ? 'success' : 'primary'}
          onClick={handleYesterdayClick}
        >
          Ontem
        </Button>

        <Button 
          className='button-date-filter'
          variant={activeFilter === 'last15' ? 'success' : 'primary'}
          onClick={handleLast15Click}
        >
          15 Dias
        </Button>

        <Button 
          className='button-date-filter'
          variant={activeFilter === 'last30' ? 'success' : 'primary'}
          onClick={handleLast30Click}
        >
          30 Dias
        </Button>

        <Button 
          className='button-date-filter long'
          variant={activeFilter === 'wholePeriod' ? 'success' : 'primary'}
          onClick={handleWholePeriodClick}
        >
          Todo o Período
        </Button>
      </Row>
    </>
  ), [activeFilter, handleTodayClick, handleYesterdayClick, handleLast15Click, handleLast30Click, handleWholePeriodClick]);

  const customFilterForm = useMemo(() => (
    <Form onSubmit={handleLoadDataCustomFilter}>
      <Row className='custom-date-filter-row'>
        <Col>
          <Form.Group controlId="formGroupDataInicio">
            <Form.Label>Data Inicial</Form.Label>
            <Form.Control
              type="date"
              name="dataInicio"
              value={initialDate}
              selected={initialDate}
              onChange={handleInitialDateChange}
              placeholder="Data de Início do Evento"
            />
            <Form.Control.Feedback type="invalid">
              <FieldValidationMessage fieldName="Data de Início" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="formGroupDataTermino">
            <Form.Label>Data Final</Form.Label>
            <Form.Control
              type="date"
              name="dataTermino"
              value={finalDate}
              selected={finalDate}
              onChange={handleFinalDateChange}
              placeholder="Data de Encerramento do Evento"
              isInvalid={new Date(finalDate).getTime() < new Date(initialDate).getTime()}
            />
            <Form.Control.Feedback type="invalid">
              <FieldValidationMessage 
                greaterThan
                fieldName="Data de Encerramento"
                fieldGreaterThan="Data de Início"
              />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col>
          <Button 
            type='submit'
            style={{marginTop: 30}}
            variant={activeFilter === 'custom' ? 'success' : 'primary'}
            onClick={handleCustomPeriodClick}
          >
            Filtrar
          </Button>
        </Col>
      </Row>
    </Form>
  ), [handleLoadDataCustomFilter, initialDate, finalDate, handleInitialDateChange, handleFinalDateChange, activeFilter, handleCustomPeriodClick]);

  const isAnyRequestLoading = useMemo(() => (
    Object.values(sectionLoading).some(Boolean)
  ), [sectionLoading]);

  if (!initialLoadDone && isLoading) {
    return <FullScreenLoading />;
  }

  if (!authorized) {
    return <Error401 />;
  }

  return (
    <>
      {isGeneratingPdf && <FullScreenLoading />}
      <Card className='dashboard-card'>
        <Card.Header>
          <h1 className='dashboard-title'>
            {eventoInfo.nome + ' - ' + eventoInfo.local}
          </h1>
          <div className='fw-600 flex-row align-center event-period-wrapper'>
            <DateColumn date={eventoInfo.dataInicio}/>
            <p className='date-spacer'>-</p>
            <DateColumn date={eventoInfo.dataTermino}/>
          </div>
          <Row className="flex-end-center mb-3">
            <Col>
              <div className='grid-actions'>
                <BackButton />
                <ReloadButton getData={loadData}/>
                <DetailButton
                  gridData={eventoInfo}
                  route={`/evento/detail/${id}`}
                  dashboardClass='pedido-button-from-dashboard'
                />
                <DownloadPDFButton generatePDF={generatePDF} disabled={isAnyRequestLoading || isGeneratingPdf} />
              </div>
            </Col>
          </Row>
          {filterButtons}
          <Row className='custom-date-filter-row lojas-dashboard-filter'>
            <Col>
              <Form.Label>Lojas</Form.Label>
              <Select
                name="lojaId"
                isMulti={true}
                value={valueLoja}
                closeMenuOnSelect
                options={comboLojaValues}
                onChange={handleLojaChange}
                classNamePrefix="react-select"
                className='select-pessoas-loja-form'
                placeholder="Selecione as lojas para filtrar"
              />
            </Col>
          </Row>
          <Row>
            {customFilterForm}
          </Row>
        </Card.Header>
        {dashboardContent}
      </Card>
    </>
  );
};

export default React.memo(EventoDashboard);